import * as vectorImage from '@wix/thunderbolt-commons/dist/vectorImage';
import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
  isStylableComponent,
} from '@wix/editor-elements-integrations';
import {
  isExperimentOpen,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import type {
  StylableButtonDefinition,
  StylableButtonPlatformProps,
  StylableButtonCarmiProps,
  StylableButtonSdkData,
  IStylableButtonStateValues,
  FlexDirection,
  LogicalAlignment,
  IStylableButtonOverflowCssVars,
} from '../StylableButton.types';
import type { Direction, WithInherit } from '@wix/editor-elements-common-utils';
import { LabelOverflow } from '../constants';
import { addAttributesToStringSvg } from '../../AnimatedSvg/utils';
import type { ParsedSvg } from '@wix/thunderbolt-commons/dist';
import { checkIfIsOneButtonSkin } from './utils';

export const props = withCompInfo<
  StylableButtonPlatformProps,
  StylableButtonDefinition,
  StylableButtonCarmiProps
>()(
  [
    'compProps',
    'isQaMode',
    'fullNameCompType',
    'language',
    'mainPageId',
    'compId',
    'experiments',
    'trackClicksAnalytics',
    'compData',
  ],
  (
    { compProps, experiments, compData, ...rest },
    { className, ...carmiData },
  ) => {
    // @ts-expect-error
    const { svgAnimatedString, animationDuration } = carmiData;
    let svgAnimatedIcon = null;
    if (svgAnimatedString && animationDuration) {
      svgAnimatedIcon = addAttributesToStringSvg(
        svgAnimatedString,
        animationDuration,
      );
    }
    return {
      ...compProps,
      ...carmiData,
      ...rest,
      direction: compData.direction,
      stylableButtonClassName: className,
      isUdpExperimentOn: isExperimentOpen(
        experiments,
        'specs.thunderbolt.buttonUdp',
      ),
      link: carmiData.link && { ...carmiData.link },
      svgAnimatedIcon,
    };
  },
);

const maxContentCSS = (isWrapText: boolean) => ({
  height: 'max-content',
  'min-width': isWrapText ? '10px' : 'max-content',
});
const getDirections = (
  dataDirection: WithInherit<Direction>,
  styleDirection: string,
) => {
  const buttonDirection = dataDirection || 'unset';
  const textDirection = dataDirection || styleDirection;
  return {
    '--btn-direction': withSiteDirection(buttonDirection),
    '--direction': textDirection,
  };
};

export const labelOverflowToCssVarsMap: Record<
  LabelOverflow,
  IStylableButtonOverflowCssVars
> = {
  [LabelOverflow.Ellipsis]: {
    '--label-text-overflow': 'ellipsis',
    '--label-white-space': 'nowrap',
    '--btn-min-width': '10px',
  },
  [LabelOverflow.Wrap]: {
    '--label-text-overflow': 'initial',
    '--label-white-space': 'pre-line',
    '--btn-min-width': 'min-content',
  },
};
export const css = withCompInfo()(
  ['compProps', 'compData', 'styleProperties', 'experiments'],
  ({
    compProps: { isMaxContent, isWrapText },
    compData,
    styleProperties,
    experiments,
  }) => {
    const hasStCSS = styleProperties['st-css'] !== undefined;
    const isBuilder = isExperimentOpen(
      experiments,
      'specs.thunderbolt.renderPlatformBuilderComponent',
    );
    if (isBuilder) {
      return {};
    }
    if (!hasStCSS) {
      const flexDirection = styleProperties[
        'container-flex-direction'
      ] as FlexDirection;
      const logicalHorizontalAlignment = styleProperties[
        'content-horizontal-alignment'
      ] as LogicalAlignment;
      const logicalHorizontalAlignmentProperty = flexDirection.startsWith('row')
        ? '--container-justify-content'
        : '--container-align-items';

      const logicalVerticalAlignmentProperty = flexDirection.startsWith('row')
        ? '--container-align-items'
        : '--container-justify-content';
      const labelOverflowValue = styleProperties['label-overflow'];

      const overflowCSSVars =
        labelOverflowToCssVarsMap[labelOverflowValue as LabelOverflow] ??
        labelOverflowToCssVarsMap[LabelOverflow.Ellipsis];
      return {
        ...getDirections(compData.direction, styleProperties.direction),
        ...overflowCSSVars,
        [logicalHorizontalAlignmentProperty]: logicalHorizontalAlignment,
        [logicalVerticalAlignmentProperty]: 'center',
        ...(isMaxContent ? maxContentCSS(isWrapText) : {}),
      };
    }
    return {
      ...(isMaxContent ? maxContentCSS(isWrapText) : {}),
    };
  },
);

export const sdkData = withCompInfo<
  StylableButtonSdkData,
  StylableButtonDefinition
>()(['compData', 'topology'], ({ compData: { svgId }, topology }) => {
  const { mediaRootUrl } = topology;
  const { getSvgBaseUrl } = vectorImage.buildSvgUrlFactory();
  return {
    mediaSvgUrl: getSvgBaseUrl(mediaRootUrl),
    svgId: (svgId as unknown as ParsedSvg)?.svgId,
  };
});

export const requiredStateRefs: Array<keyof IStylableButtonStateValues> = [
  'reportBi',
  'reducedMotion',
];

export const stateRefs =
  withStateRefsValues<keyof IStylableButtonStateValues>(requiredStateRefs);

export const uiType = withCompInfo<any, any>()(
  ['styleProperties', 'skin', 'experiments'],
  ({ styleProperties, skin, experiments }) => {
    const BUILDER_SPEC = 'specs.thunderbolt.renderPlatformBuilderComponent';
    const isBuilderEnv = isExperimentOpen(experiments, BUILDER_SPEC);

    if (checkIfIsOneButtonSkin(skin)) {
      return 'Button';
    }

    if (isBuilderEnv) {
      return 'StylableButton';
    }

    return isStylableComponent(styleProperties) ? 'Default' : 'StylableButton';
  },
);

export default createComponentMapperModel({
  props,
  css,
  sdkData,
  stateRefs,
  uiType,
});
